import React, { useEffect, useState } from 'react';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useToast } from '../../context/ToastContext';
import { useDialog } from '../../../context/DialogContext';

const EMPTY_FORM = { name: '', address_line1: '', city: '', district: '', formatted_address: '', delivery_instructions: '', latitude: '', longitude: '', is_default: false };

export default function AddressesPage() {
  const { apiCall }  = useCustomerAuth();
  const toast        = useToast();
  const { confirm }  = useDialog();
  const [sites,      setSites]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showModal,  setShowModal]  = useState(false);
  const [editSite,   setEditSite]   = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [deleting,   setDeleting]   = useState(null);

  const load = async () => {
    try { setSites(await apiCall('/customer/sites')); }
    catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openAdd  = () => { setForm(EMPTY_FORM); setEditSite(null); setShowModal(true); };
  const openEdit = s  => { setForm({ name: s.name, address_line1: s.address_line1 || '', city: s.city || '', district: s.district || '', formatted_address: s.formatted_address || '', delivery_instructions: s.delivery_instructions || '', latitude: s.latitude || '', longitude: s.longitude || '', is_default: s.is_default }); setEditSite(s); setShowModal(true); };

  const set = k => e => setForm(p => ({ ...p, [k]: k === 'is_default' ? e.target.checked : e.target.value }));

  const save = async e => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('أدخل اسم العنوان'); return; }
    if (!form.address_line1.trim() && !form.formatted_address.trim()) { toast.error('أدخل العنوان'); return; }
    setSaving(true);
    try {
      const payload = { ...form, latitude: form.latitude ? Number(form.latitude) : null, longitude: form.longitude ? Number(form.longitude) : null };
      if (editSite) {
        await apiCall(`/customer/sites/${editSite.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('تم تحديث العنوان');
      } else {
        await apiCall('/customer/sites', { method: 'POST', body: JSON.stringify(payload) });
        toast.success('تم إضافة العنوان');
      }
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const deleteS = async (id) => {
    if (!await confirm({ title: 'حذف العنوان', message: 'هل تريد حذف هذا العنوان؟', type: 'delete', confirmText: 'حذف', cancelText: 'تراجع' })) return;
    setDeleting(id);
    try { await apiCall(`/customer/sites/${id}`, { method: 'DELETE' }); toast.success('تم الحذف'); load(); }
    catch (err) { toast.error(err.message); }
    finally { setDeleting(null); }
  };

  const setDefault = async (id) => {
    try { await apiCall(`/customer/sites/${id}/default`, { method: 'PUT' }); load(); }
    catch (err) { toast.error(err.message); }
  };

  const T = {
    text:    'var(--wt-text-primary)',
    muted:   'var(--wt-text-muted)',
    surface: 'var(--wt-surface)',
    border:  'var(--wt-border)',
  };

  const inputSt = {
    width:'100%', padding:'0.72rem 0.9rem', borderRadius:'0.75rem', outline:'none',
    background:'rgba(255,255,255,0.05)', border:'1.5px solid var(--wt-border)',
    color: T.text, fontSize:'0.875rem', fontWeight:600,
    fontFamily:"'Cairo',sans-serif", boxSizing:'border-box',
    transition:'border 0.2s, box-shadow 0.2s',
  };

  const PinIcon = ({ active }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke={active ? '#fff' : '#0ABAB5'} strokeWidth="1.8" strokeLinecap="round">
      <path d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"/>
      <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"/>
    </svg>
  );

  return (
    <div dir="rtl" style={{ display:'flex', flexDirection:'column', gap:'1rem', fontFamily:"'Cairo',sans-serif" }}>
      <style>{`
        @keyframes addr-in { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes addr-spin { to{transform:rotate(360deg)} }
        @keyframes sheet-in { from{transform:translateY(60px);opacity:0} to{transform:translateY(0);opacity:1} }
        .addr-card:hover { box-shadow:0 8px 32px rgba(10,186,181,0.12) !important; }
        .addr-input:focus { border-color:rgba(10,186,181,0.7) !important; box-shadow:0 0 0 3px rgba(10,186,181,0.12) !important; }
        .addr-input::placeholder { opacity:0.35; }
      `}</style>

      {/* ══ HEADER ══ */}
      <div style={{
        borderRadius:'1.375rem', overflow:'hidden', position:'relative',
        background:'linear-gradient(135deg,#062040 0%,#083060 45%,#0a3d75 100%)',
        boxShadow:'0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(10,186,181,0.15)',
        animation:'addr-in 0.35s ease',
      }}>
        <div style={{ height:3, background:'linear-gradient(90deg,transparent,#0ABAB5 40%,rgba(56,189,248,0.7),transparent)' }}/>
        <div style={{ padding:'1.1rem 1.25rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:'0.62rem', color:'rgba(186,230,253,0.45)', fontWeight:700, letterSpacing:'0.1em', marginBottom:'0.2rem' }}>WTRLY</div>
            <div style={{ fontSize:'1.2rem', fontWeight:900, color:'#fff', lineHeight:1.1 }}>عناويني</div>
            <div style={{ fontSize:'0.7rem', color:'rgba(186,230,253,0.45)', marginTop:'0.25rem' }}>
              {loading ? '...' : `${sites.length} عنوان محفوظ`}
            </div>
          </div>
          <button onClick={openAdd} style={{
            display:'flex', alignItems:'center', gap:'0.4rem',
            padding:'0.6rem 1rem', borderRadius:'0.875rem', border:'none', cursor:'pointer',
            background:'rgba(10,186,181,0.2)', color:'#0ABAB5',
            fontSize:'0.82rem', fontWeight:800, fontFamily:"'Cairo',sans-serif",
            border:'1px solid rgba(10,186,181,0.4)',
            boxShadow:'0 0 14px rgba(10,186,181,0.2)',
            transition:'all 0.18s',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 4v16M4 12h16"/></svg>
            إضافة
          </button>
        </div>
      </div>

      {/* ══ LOADING ══ */}
      {loading && (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem' }}>
          {[1,2].map(i => (
            <div key={i} style={{ height:110, borderRadius:'1.1rem', background:'var(--wt-surface)', border:'1px solid var(--wt-border)', animation:'addr-in 0.3s ease', opacity:0.5 }}/>
          ))}
        </div>
      )}

      {/* ══ EMPTY ══ */}
      {!loading && sites.length === 0 && (
        <div style={{
          borderRadius:'1.1rem', padding:'3rem 1.5rem', textAlign:'center',
          background:'var(--wt-surface)', border:'1px solid var(--wt-border)',
          animation:'addr-in 0.35s ease',
        }}>
          <div style={{
            width:64, height:64, borderRadius:'50%', margin:'0 auto 1rem',
            background:'rgba(10,186,181,0.08)', border:'1px solid rgba(10,186,181,0.2)',
            display:'flex', alignItems:'center', justifyContent:'center',
            boxShadow:'0 0 24px rgba(10,186,181,0.1)',
          }}>
            <PinIcon active={false}/>
          </div>
          <p style={{ color: T.text, fontWeight:800, margin:'0 0 0.35rem', fontSize:'0.95rem' }}>لا توجد عناوين</p>
          <p style={{ color: T.muted, fontSize:'0.8rem', margin:'0 0 1.25rem' }}>أضف موقع التوصيل المفضّل</p>
          <button onClick={openAdd} style={{
            padding:'0.7rem 1.75rem', borderRadius:'0.875rem', border:'none', cursor:'pointer',
            background:'linear-gradient(135deg,#0ABAB5,#0891b2)', color:'#fff',
            fontSize:'0.875rem', fontWeight:800, fontFamily:"'Cairo',sans-serif",
            boxShadow:'0 4px 16px rgba(10,186,181,0.3)',
          }}>إضافة عنوان</button>
        </div>
      )}

      {/* ══ ADDRESS CARDS ══ */}
      {!loading && sites.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
          {sites.map((site, i) => (
            <div key={site.id} className="addr-card" style={{
              borderRadius:'1.1rem', overflow:'hidden',
              background:'var(--wt-surface)',
              border:`1.5px solid ${site.is_default ? 'rgba(10,186,181,0.4)' : 'var(--wt-border)'}`,
              boxShadow: site.is_default ? '0 0 24px rgba(10,186,181,0.12)' : 'none',
              animation:`addr-in 0.3s ease ${i*0.05}s both`,
              transition:'box-shadow 0.2s',
            }}>
              {/* Default accent bar */}
              {site.is_default && (
                <div style={{ height:2.5, background:'linear-gradient(90deg,transparent,#0ABAB5,rgba(56,189,248,0.7),transparent)' }}/>
              )}

              <div style={{ padding:'0.875rem 1rem' }}>
                {/* Top row */}
                <div style={{ display:'flex', alignItems:'flex-start', gap:'0.75rem' }}>
                  <div style={{
                    width:42, height:42, borderRadius:'0.875rem', flexShrink:0,
                    background: site.is_default ? 'linear-gradient(135deg,#0ABAB5,#0891b2)' : 'rgba(10,186,181,0.08)',
                    border: `1px solid ${site.is_default ? 'rgba(10,186,181,0.5)' : 'rgba(10,186,181,0.2)'}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    boxShadow: site.is_default ? '0 0 16px rgba(10,186,181,0.35)' : 'none',
                  }}>
                    <PinIcon active={site.is_default}/>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap', marginBottom:'0.25rem' }}>
                      <span style={{ color: T.text, fontWeight:800, fontSize:'0.9rem' }}>{site.name}</span>
                      {site.is_default && (
                        <span style={{
                          fontSize:'0.6rem', fontWeight:800, borderRadius:999,
                          padding:'0.12rem 0.55rem', lineHeight:1.6,
                          background:'rgba(10,186,181,0.15)', color:'#0ABAB5',
                          border:'1px solid rgba(10,186,181,0.35)',
                        }}>افتراضي</span>
                      )}
                    </div>
                    <p style={{ color: T.muted, fontSize:'0.75rem', margin:0, lineHeight:1.5 }}>
                      {site.formatted_address || site.address_line1}
                    </p>
                    {site.city && (
                      <p style={{ color: T.muted, fontSize:'0.7rem', margin:'0.1rem 0 0', opacity:0.7 }}>
                        {site.district ? `${site.district}، ` : ''}{site.city}
                      </p>
                    )}
                    {site.delivery_instructions && (
                      <div style={{
                        marginTop:'0.5rem', padding:'0.4rem 0.6rem', borderRadius:'0.5rem',
                        background:'rgba(10,186,181,0.06)', border:'1px solid rgba(10,186,181,0.12)',
                        display:'flex', alignItems:'flex-start', gap:'0.4rem',
                      }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="2" strokeLinecap="round" style={{ flexShrink:0, marginTop:2 }}><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                        <span style={{ color:'rgba(10,186,181,0.8)', fontSize:'0.68rem', fontStyle:'italic' }}>{site.delivery_instructions}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.875rem', paddingTop:'0.75rem', borderTop:'1px solid var(--wt-border)' }}>
                  {!site.is_default && (
                    <button onClick={() => setDefault(site.id)} style={{
                      flex:1, padding:'0.5rem 0.25rem', borderRadius:'0.65rem', border:'1px solid rgba(10,186,181,0.25)',
                      background:'rgba(10,186,181,0.07)', color:'#0ABAB5',
                      fontSize:'0.68rem', fontWeight:800, cursor:'pointer', fontFamily:"'Cairo',sans-serif",
                      display:'flex', alignItems:'center', justifyContent:'center', gap:'0.3rem',
                    }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                      افتراضي
                    </button>
                  )}
                  <button onClick={() => openEdit(site)} style={{
                    flex:1, padding:'0.5rem 0.25rem', borderRadius:'0.65rem', border:'1px solid var(--wt-border)',
                    background:'rgba(255,255,255,0.04)', color: T.muted,
                    fontSize:'0.68rem', fontWeight:800, cursor:'pointer', fontFamily:"'Cairo',sans-serif",
                    display:'flex', alignItems:'center', justifyContent:'center', gap:'0.3rem',
                  }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    تعديل
                  </button>
                  <button onClick={() => deleteS(site.id)} disabled={deleting === site.id} style={{
                    flex:1, padding:'0.5rem 0.25rem', borderRadius:'0.65rem', border:'1px solid rgba(244,63,94,0.2)',
                    background:'rgba(244,63,94,0.06)', color:'#f43f5e',
                    fontSize:'0.68rem', fontWeight:800, cursor: deleting===site.id ? 'not-allowed':'pointer',
                    opacity: deleting===site.id ? 0.5 : 1, fontFamily:"'Cairo',sans-serif",
                    display:'flex', alignItems:'center', justifyContent:'center', gap:'0.3rem',
                  }}>
                    {deleting === site.id
                      ? <span style={{ width:10, height:10, borderRadius:'50%', border:'2px solid rgba(244,63,94,0.3)', borderTopColor:'#f43f5e', animation:'addr-spin 0.7s linear infinite', display:'inline-block' }}/>
                      : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
                    }
                    {deleting === site.id ? '' : 'حذف'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ══ BOTTOM SHEET MODAL ══ */}
      {showModal && (
        <div style={{
          position:'fixed', inset:0, zIndex:9999,
          display:'flex', alignItems:'flex-end',
          background:'rgba(0,0,0,0.65)', backdropFilter:'blur(6px)',
        }} dir="rtl" onClick={() => setShowModal(false)}>
          <div style={{
            width:'100%', maxHeight:'92vh', overflowY:'auto',
            background:'var(--wt-surface, #0b1e30)',
            borderRadius:'1.375rem 1.375rem 0 0',
            boxShadow:'0 -16px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(10,186,181,0.12)',
            animation:'sheet-in 0.3s cubic-bezier(0.34,1.2,0.64,1)',
          }} onClick={e => e.stopPropagation()}>

            {/* Drag handle */}
            <div style={{ display:'flex', justifyContent:'center', padding:'0.75rem 0 0.25rem' }}>
              <div style={{ width:36, height:4, borderRadius:999, background:'rgba(255,255,255,0.12)' }}/>
            </div>
            <div style={{ height:2, background:'linear-gradient(90deg,transparent,#0ABAB5 40%,rgba(56,189,248,0.7),transparent)', margin:'0 1rem', borderRadius:999 }}/>

            {/* Sheet Header */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.875rem 1.1rem 0.5rem' }}>
              <div>
                <div style={{ fontSize:'0.6rem', color:'rgba(10,186,181,0.6)', fontWeight:700, letterSpacing:'0.1em' }}>WTRLY</div>
                <div style={{ fontSize:'1rem', fontWeight:900, color: T.text }}>
                  {editSite ? 'تعديل العنوان' : 'إضافة عنوان جديد'}
                </div>
              </div>
              <button onClick={() => setShowModal(false)} style={{
                width:32, height:32, borderRadius:'50%', border:'none', cursor:'pointer',
                background:'rgba(255,255,255,0.07)', color: T.muted,
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem',
              }}>✕</button>
            </div>

            {/* Form */}
            <form onSubmit={save} style={{ padding:'0.75rem 1.1rem 2.5rem', display:'flex', flexDirection:'column', gap:'0.75rem' }}>
              {[
                { label:'اسم الموقع', key:'name', placeholder:'مثال: المنزل، المكتب...', required:true },
                { label:'العنوان التفصيلي', key:'address_line1', placeholder:'رقم المبنى، اسم الشارع...' },
                { label:'الحي', key:'district', placeholder:'اسم الحي' },
                { label:'المدينة', key:'city', placeholder:'الرياض' },
                { label:'العنوان الكامل للعرض', key:'formatted_address', placeholder:'العنوان كاملاً' },
                { label:'تعليمات التوصيل', key:'delivery_instructions', placeholder:'مثال: الباب الجانبي...' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, color: T.muted, marginBottom:'0.35rem' }}>
                    {f.label} {f.required && <span style={{ color:'#f43f5e' }}>*</span>}
                  </label>
                  <input className="addr-input" type="text" value={form[f.key]} onChange={set(f.key)}
                    placeholder={f.placeholder} required={f.required}
                    style={{ ...inputSt }}/>
                </div>
              ))}

              {/* Default toggle */}
              <div style={{
                display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'0.65rem 0.875rem', borderRadius:'0.75rem',
                background:'rgba(10,186,181,0.06)', border:'1px solid rgba(10,186,181,0.15)',
                cursor:'pointer',
              }} onClick={() => setForm(p => ({ ...p, is_default: !p.is_default }))}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="2" strokeLinecap="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                  <span style={{ fontSize:'0.8rem', fontWeight:700, color: T.text }}>تعيين كعنوان افتراضي</span>
                </div>
                {/* Toggle switch */}
                <div style={{
                  width:40, height:22, borderRadius:999, position:'relative',
                  background: form.is_default ? '#0ABAB5' : 'rgba(255,255,255,0.1)',
                  border:`1.5px solid ${form.is_default ? '#0ABAB5' : 'rgba(255,255,255,0.15)'}`,
                  transition:'background 0.2s, border 0.2s',
                }}>
                  <div style={{
                    position:'absolute', top:2, width:14, height:14, borderRadius:'50%', background:'#fff',
                    left: form.is_default ? 22 : 2,
                    transition:'left 0.2s', boxShadow:'0 1px 4px rgba(0,0,0,0.3)',
                  }}/>
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display:'flex', gap:'0.625rem', paddingTop:'0.25rem' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{
                  flex:1, padding:'0.8rem', borderRadius:'0.875rem', border:'1px solid var(--wt-border)',
                  background:'rgba(255,255,255,0.05)', color: T.muted,
                  fontSize:'0.875rem', fontWeight:700, cursor:'pointer', fontFamily:"'Cairo',sans-serif",
                }}>إلغاء</button>
                <button type="submit" disabled={saving} style={{
                  flex:2, padding:'0.8rem', borderRadius:'0.875rem', border:'none', cursor: saving?'not-allowed':'pointer',
                  background:'linear-gradient(135deg,#0ABAB5,#0891b2)', color:'#fff',
                  fontSize:'0.875rem', fontWeight:800, fontFamily:"'Cairo',sans-serif",
                  boxShadow:'0 4px 16px rgba(10,186,181,0.3)', opacity: saving?0.65:1,
                  display:'flex', alignItems:'center', justifyContent:'center', gap:'0.4rem',
                }}>
                  {saving
                    ? <><span style={{ width:14, height:14, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'addr-spin 0.7s linear infinite', display:'inline-block' }}/> جاري الحفظ...</>
                    : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 13l4 4L19 7"/></svg> حفظ العنوان</>
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
